import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { AgentResponseSegment } from '../../utils/agent-client';
import { fetchPendingAgentOrders, QUERY_KEY } from '../AgentOrderApproval/AgentOrderApproval';
import {
  AuditStreamEvent,
  deriveAuditEvents,
  GovernanceAuditEvent,
  mapRealAuditEvents
} from './audit-stream';

// The live audit trail — a time-ordered feed of the governed actions behind the
// plan: each scope-check, every tool call, the human gate decisions, and the order
// lifecycle. This is the "governance instrumented while the agent runs" evidence
// Forrester asks for. When the governed runtime streams its real AuditEvents
// (governance.audit CustomEvents) these render byte-identical to CloudWatch —
// including any denial. Until then it falls back to a feed derived from the real
// tool-call stream + order state, explicitly labeled as derived.
interface AuditStreamPanelProps {
  company: string;
  segments: AgentResponseSegment[];
  newtonReplayed: boolean;
  govAuditEvents: GovernanceAuditEvent[];
}

export const AuditStreamPanel = ({
  company,
  segments,
  newtonReplayed,
  govAuditEvents
}: AuditStreamPanelProps) => {
  const { data: orders } = useQuery({
    queryKey: [QUERY_KEY, company],
    queryFn: () => fetchPendingAgentOrders(company),
    enabled: Boolean(company)
  });

  // Prefer the agent's real backend AuditEvents; fall back to the derived feed only
  // when none have arrived (older runtime, or before the run emits any).
  const realEvents = mapRealAuditEvents(govAuditEvents);
  const isLive = realEvents.length > 0;
  const events: AuditStreamEvent[] = isLive
    ? realEvents
    : deriveAuditEvents({ segments, newtonReplayed, orders });

  return (
    <div className='elevate-audit'>
      <div className='elevate-audit-head'>
        <h2 className='elevate-audit-title'>
          Audit trail — governance as it ran{' '}
          <span className='elevate-audit-kind'>{isLive ? 'live' : 'derived'}</span>
        </h2>
        <p className='elevate-audit-sub'>
          {isLive
            ? 'Real AuditEvents streamed from the governed runtime as it ran — the same records written to CloudWatch. Scope-checks, gate decisions, and any guardrail blocks appear here exactly as the backend recorded them.'
            : "Derived from the agent's real tool-call stream and live order state; the runtime's raw AuditEvents render here (byte-identical to CloudWatch) once the run emits them."}
        </p>
      </div>
      {events.length === 0 ? (
        <p className='elevate-audit-empty'>
          No governed actions yet — the trail fills in as the planner runs and drafts orders.
        </p>
      ) : (
        <ol className='elevate-audit-list'>
          {events.map(event => (
            <AuditRow key={event.id} event={event} />
          ))}
        </ol>
      )}
    </div>
  );
};

interface AuditRowProps {
  event: AuditStreamEvent;
}

const AuditRow = ({ event }: AuditRowProps) => (
  <li className={cn('elevate-audit-row', `is-${event.kind}`)}>
    <span className='elevate-audit-kind'>{event.kind}</span>
    <div className='elevate-audit-body'>
      <span className='elevate-audit-label'>{event.label}</span>
      <span className='elevate-audit-detail'>{event.detail}</span>
    </div>
  </li>
);
